import React, { useEffect, useState, useMemo } from "react";
import { Button, Dropdown, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../firebase/AuthProvider";
import { GetFilesByParticipantsRes } from "../../../../interfaces/Participant";
import { GetFilesByParticipantsAsync } from "../../../../services/participantServices/ParticipantService";
import { getTaskById } from "../../../../services/taskServices/TaskService";
import { toast } from "react-toastify";
import moment from "moment";
import {
  deleteFileById,
  downloadFile,
  getCanUserDeleteTaskFile,
} from "../../../../services/fileServices/FileService";
import { showConfirmationDialog } from "../../../modal/ConfimationModal/Confimation";
import { Download, Eye, PenLine, Signature, Trash2 } from "lucide-react";
import { PaginationDto } from "../../../../interfaces/Employee/Employee";
import NoParticipantsFound from "../../participantfiles/noParticipantsFound";
import "../../participantfiles/noParticipantsFound.css";
import { formatFileSize } from "../../../../utilities/fileUtils";

interface RecentFileTabProps {
  authToken: string;
  participantId: string;
  taskId?: string;
  pagination?: { pageNumber: number; pageSize: number };
  setPagination?: React.Dispatch<React.SetStateAction<{ pageNumber: number; pageSize: number }>> | ((pagination: { pageNumber: number; pageSize: number }) => void);
  setTotalItems?: React.Dispatch<React.SetStateAction<number>> | ((total: number) => void);
}

const getFileIcon = (filename1: string) => {
  let filename = filename1 ? filename1.toLocaleLowerCase() : "";
  if (filename.endsWith("pdf")) {
    return (
      <div className="ps-file-icon-wrapper pdf">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M4 2C4 1.44772 4.44772 1 5 1H15C15.5523 1 16 1.44772 16 2V18C16 18.5523 15.5523 19 15 19H5C4.44772 19 4 18.5523 4 18V2Z"
            fill="#E7000B"
          />
          <path
            d="M7 6H13M7 9H11"
            stroke="white"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  } else if (filename.endsWith("docx") || filename.endsWith("doc")) {
    return (
      <div className="ps-file-icon-wrapper docx">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M4 2C4 1.44772 4.44772 1 5 1H15C15.5523 1 16 1.44772 16 2V18C16 18.5523 15.5523 19 15 19H5C4.44772 19 4 18.5523 4 18V2Z"
            fill="#155DFC"
          />
          <path
            d="M7 6H13M7 9H11"
            stroke="white"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  } else if (
    filename.endsWith("xlsx") ||
    filename.endsWith("xls") ||
    filename.endsWith("csv")
  ) {
    return (
      <div className="ps-file-icon-wrapper xlsx">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M4 2C4 1.44772 4.44772 1 5 1H15C15.5523 1 16 1.44772 16 2V18C16 18.5523 15.5523 19 15 19H5C4.44772 19 4 18.5523 4 18V2Z"
            fill="#00A63E"
          />
          <path
            d="M7 6H13M7 9H11"
            stroke="white"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  } else {
    return (
      <div className="ps-file-icon-wrapper other">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M4 2C4 1.44772 4.44772 1 5 1H15C15.5523 1 16 1.44772 16 2V18C16 18.5523 15.5523 19 15 19H5C4.44772 19 4 18.5523 4 18V2Z"
            fill="#665959"
          />
          <path
            d="M7 6H13M7 9H11"
            stroke="white"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  }
};

const RecentFileTab = ({
  authToken,
  participantId,
  taskId,
  pagination: externalPagination,
  setPagination: setExternalPagination,
  setTotalItems: setExternalTotalItems,
}: RecentFileTabProps) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [participantFiles, setParticipantFiles] = useState<
    GetFilesByParticipantsRes[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [canUserDeleteFile, setCanUserDeleteFile] = useState<boolean>(false);
  const [filePermissions, setFilePermissions] = useState<Record<string, boolean>>({});
  const [internalPagination, setInternalPagination] = useState<PaginationDto>({ pageNumber: 1, pageSize: 10 });

  // Use external pagination if provided, otherwise use internal
  const pagination = externalPagination || internalPagination;
  const setPagination = setExternalPagination || setInternalPagination;

  useEffect(() => {
    if (!authToken) return;
    getParticipantFiles();
    if (taskId) fetchCanUserDeleteTaskFiles();
  }, [authToken, participantId, taskId, pagination.pageNumber, pagination.pageSize]);


  const getParticipantFiles = () => {
    setLoading(true);
    GetFilesByParticipantsAsync(authToken, participantId, pagination.pageNumber, pagination.pageSize)
      .then((response) => {
        if (response.success) {
          setParticipantFiles(response.data.files);
          const totalfiles = response.data.count;
          if (setExternalTotalItems) {
            setExternalTotalItems(totalfiles);
          }
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchCanUserDeleteTaskFiles = async () => {
    try {
      if (taskId) {
        const response = await getCanUserDeleteTaskFile(authToken, taskId);
        setCanUserDeleteFile(response);
      }
    } catch (error) {
      console.error("Error fetching delete permission:", error);
    }
  };

  const handleView = (fileId: string) => navigate(`/view/file/${fileId}`);

  const handleDownload = async (fileId: string, filename: string) => {
    try {
      const blob = await downloadFile(fileId, authToken);
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file.");
    }
  };

  const handleSignatureRequest = (fileId: string) =>
    navigate(`/edit/file/${fileId}`);

  const handleClickDelete = async (fileId: string) => {
    const isConfirm = await showConfirmationDialog(
      "Are you sure you want to delete this file?"
    );
    if (!isConfirm) return;
    try {
      setLoading(true);
      await deleteFileById(fileId, authToken);
      toast.success("File Deleted Successfully");
      getParticipantFiles();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete file.");
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <div className="text-center py-4">
        <Spinner size="sm" /> Loading...
      </div>
    );

  return (
    <div className="task-attachment-section !mb-0">
      {/* <div className="attachment-summary mt-[1rem] mb-0 !important">
        <span>
          {totalFiles} files • {totalSizeMB} MB total
        </span>
      </div> */}

      {/* <div className="ps-files-filters">
        <button className="ps-file-filter-dropdown" type="button">
          <span>Uploaded by</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 9L12 15L6 9" stroke="#666666" strokeWidth="2.004" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button className="ps-file-filter-dropdown" type="button">
          <span>Status</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 9L12 15L6 9" stroke="#666666" strokeWidth="2.004" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button className="ps-file-filter-dropdown" type="button">
          <span>Date</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 9L12 15L6 9" stroke="#666666" strokeWidth="2.004" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button className="ps-file-filter-dropdown" type="button">
          <span>Required Contacts</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 9L12 15L6 9" stroke="#666666" strokeWidth="2.004" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div> */}

      <div className="attachment-list">
        {!loading && participantFiles.length === 0 ? (
          <div className="no-participants-found-container">
            <NoParticipantsFound />
          </div>
        ) : (
          participantFiles.map((item) => (
            <div key={item.id} className="ps-file-item">
              {/* {getFileIcon(item.fileName)} */}
              <div className="ps-file-change-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M4.80078 4.80002C4.80078 4.1635 5.05364 3.55306 5.50372 3.10297C5.95381 2.65288 6.56426 2.40002 7.20078 2.40002H12.704C13.3404 2.40016 13.9508 2.65311 14.4008 3.10322L18.4976 7.20002C18.9477 7.65001 19.2006 8.26036 19.2008 8.89682V19.2C19.2008 19.8365 18.9479 20.447 18.4978 20.8971C18.0478 21.3472 17.4373 21.6 16.8008 21.6H7.20078C6.56426 21.6 5.95381 21.3472 5.50372 20.8971C5.05364 20.447 4.80078 19.8365 4.80078 19.2V4.80002Z" fill="#C9C9C9" />
                </svg>
              </div>

              <div className="ps-file-details">
                <h4 className="ps-file-name">{item.fileName}</h4>
                <div
                  className="ps-file-meta"
                  style={{
                    display: "flex",
                    height: "1rem",
                    alignItems: "center",
                    gap: "0.75rem",
                    flexShrink: 0,
                    alignSelf: "stretch",
                    color: "#6A7282",
                    fontFamily: "Arial",
                    fontSize: "0.75rem",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "1rem",
                  }}
                >
                  {/* <span>
                    {(() => {
                      let fileSizeKB = 0;
                      let fileSizeDisplay = "--";
                      if (item.size) {
                        fileSizeKB = Math.round(item.size / 1024);
                        fileSizeDisplay = fileSizeKB >= 1024
                          ? `${(fileSizeKB / 1024).toFixed(1)} MB`
                          : `${fileSizeKB} KB`;
                      }
                      return fileSizeDisplay;
                    })()}
                  </span> */}
                  <span>{formatFileSize(item.size)}</span>
                  <span>•</span>
                  <span>Uploaded by {item.authorName}</span>
                  <span>•</span>
                  <span>{moment(item.updateDate || item.createDate).fromNow()}</span>
                </div>
              </div>

              {/* Signature indicator - you can add this field to your interface if needed */}
              {(item as any).pendingSignatureRequest === true && filePermissions[item.id] && (
                <div
                  title="Signature Pending"
                  className="ps-download-btn flex items-center gap-1"
                >
                  <Signature size={12} /> Signed
                </div>
              )}

              <button
                className="ps-download-btn"
                onClick={() => handleDownload(item.id, item.fileName)}
              >
                Download
              </button>

              <Dropdown align="end" className="file-options-dropdown">
                <Dropdown.Toggle variant="link" id={`dropdown-file-${item.id}`}>
                  <FontAwesomeIcon icon={faEllipsisV} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleView(item.id)}>
                    <Eye size={18} />
                    <span>View</span>
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleDownload(item.id, item.fileName)}
                  >
                    <Download size={18} />
                    <span>Download</span>
                  </Dropdown.Item>
                  {filePermissions[item.id] && !(item as any).pendingSignatureRequest && (
                    <Dropdown.Item
                      onClick={() => handleSignatureRequest(item.id)}
                      title="Request a signature for this document."
                    >
                      <Signature size={18} />
                      <span>Sign</span>
                    </Dropdown.Item>
                  )}
                  {canUserDeleteFile && (
                    <Dropdown.Item onClick={() => handleClickDelete(item.id)}>
                      <Trash2 size={18} />
                      <span>Delete</span>
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentFileTab;
